<template>
  <v-app>
    <v-main>
      <v-container fluid class="request-demo-container">
        <div class="background-layer">
          <v-img :src="backgroundImage" cover class="background-image">
            <div class="background-overlay"></div>
          </v-img>
        </div>

        <div class="language-toggle-wrapper">
          <v-btn-toggle v-model="currentLanguage" mandatory variant="outlined" density="compact" class="language-toggle">
            <v-btn value="en" @click="changeLanguage('en')" size="small" :class="{ 'active-lang': currentLanguage === 'en' }">
              <v-img :src="usa_flag" width="20" height="15" class="flag-img" />
              <span class="ml-1">EN</span>
            </v-btn>
            <v-btn value="de" @click="changeLanguage('de')" size="small" :class="{ 'active-lang': currentLanguage === 'de' }">
              <v-img :src="german_flag" width="20" height="15" class="flag-img" />
              <span class="ml-1">DE</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <v-btn icon variant="text" class="back-button" size="large" @click="goBack">
          <v-icon color="white" size="large">mdi-arrow-left</v-icon>
        </v-btn>

        <div class="content-wrapper">
          <div class="text-center mb-6 welcome-text">
            <h1 class="page-title text-white font-weight-bold mb-2">
              {{ $t ? $t('contact-forms.title') : 'Request a Demo' }}
            </h1>
            <p class="page-subtitle text-white mb-0">
              {{ $t ? $t('contact-forms.subtitle') : 'Fill out the form below to get started with PIA' }}
            </p>
          </div>

          <v-card elevation="24" rounded="xl" class="demo-card">
            <v-card-text class="card-content">
              <div class="text-center mb-5 logo-section">
                <v-img :src="loginLogo" max-height="70" max-width="70" class="mx-auto" contain />
              </div>

              <div v-if="submitStatus === 'success'" class="status-container">
                <div class="success-animation">
                  <v-icon size="80" color="success">mdi-check-circle</v-icon>
                </div>
                <h3 class="status-title">{{ $t ? $t('contact-forms.success.title') : 'Request Submitted!' }}</h3>
                <p class="status-message">
                  {{ $t ? $t('contact-forms.success.message') : 'Your demo request has been received. Redirecting you to complete registration...' }}
                </p>
                <v-progress-linear indeterminate color="primary" class="mt-4"></v-progress-linear>
              </div>

              <div v-else-if="submitStatus === 'error'" class="status-container">
                <div class="error-animation">
                  <v-icon size="80" color="error">mdi-alert-circle</v-icon>
                </div>
                <h3 class="status-title error-title">{{ $t ? $t('contact-forms.error.title') : 'Submission Failed' }}</h3>
                <p class="status-message">{{ errorMessage }}</p>
                <v-btn color="primary" variant="elevated" @click="resetForm" class="mt-4">
                  {{ $t ? $t('contact-forms.error.tryAgain') : 'Try Again' }}
                </v-btn>
              </div>

              <v-form v-else ref="formRef" @submit.prevent="handleSubmit">
                <div class="form-intro mb-5">
                  <v-icon color="primary" size="small" class="mr-2">mdi-information</v-icon>
                  <span class="form-intro-text">
                    {{ $t ? $t('contact-forms.formIntro') : "Fill out the form below to request access to the PIA demo. We'll set up your account right away!" }}
                  </span>
                </div>

                <v-row class="mb-2">
                  <v-col cols="12" md="6" class="py-1">
                    <v-text-field
                      v-model="formData.name"
                      :label="$t ? $t('contact-forms.form.name.label') : 'Full Name'"
                      :placeholder="$t ? $t('contact-forms.form.name.placeholder') : 'John Doe'"
                      :rules="[rules.required, rules.minLength(2)]"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-account"
                      required
                      :disabled="isSubmitting"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6" class="py-1">
                    <v-text-field
                      v-model="formData.email"
                      :label="$t ? $t('contact-forms.form.email.label') : 'Email Address'"
                      :placeholder="'john@example.com'"
                      type="email"
                      :rules="[rules.required, rules.email, rules.emailNotExists]"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-email"
                      required
                      :disabled="isSubmitting"
                      :loading="isCheckingEmail"
                      hide-details="auto"
                    >
                      <template v-slot:append-inner>
                        <v-icon v-if="formData.email && !isCheckingEmail && emailExists" color="error" size="small">mdi-alert-circle</v-icon>
                        <v-icon v-else-if="formData.email && !isCheckingEmail && !emailExists && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)" color="success" size="small">
                          mdi-check-circle
                        </v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row class="mb-2">
                  <v-col cols="12" md="6" class="py-1">
                    <v-text-field
                      v-model="formData.company"
                      :label="$t ? $t('contact-forms.form.company.label') : 'Company (Optional)'"
                      :placeholder="$t ? $t('contact-forms.form.company.placeholder') : 'Acme Inc'"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-office-building"
                      :disabled="isSubmitting"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6" class="py-1">
                    <v-text-field
                      v-model="formData.phone"
                      :label="$t ? $t('contact-forms.form.phone.label') : 'Phone Number (Optional)'"
                      :placeholder="$t ? $t('contact-forms.form.phone.placeholder') : '+49 123 456789'"
                      type="tel"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-phone"
                      :disabled="isSubmitting"
                      hide-details="auto"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="formData.message"
                  :label="$t ? $t('contact-forms.form.message.label') : 'Message (Optional)'"
                  :placeholder="$t ? $t('contact-forms.form.message.placeholder') : 'Tell us about your needs...'"
                  variant="outlined"
                  density="comfortable"
                  prepend-inner-icon="mdi-message-text-outline"
                  :disabled="isSubmitting"
                  :rules="[rules.maxLength(1000)]"
                  counter="1000"
                  rows="3"
                  auto-grow
                  hide-details="auto"
                  class="mb-3"
                ></v-textarea>

                <v-alert v-if="formError" type="warning" variant="tonal" density="compact" class="mb-4" closable @click:close="formError = ''">
                  {{ formError }}
                </v-alert>

                <v-row class="form-actions">
                  <v-col cols="12" md="6" class="py-1">
                    <v-btn type="submit" color="primary" variant="elevated" :loading="isSubmitting" :disabled="isSubmitting || emailExists" block size="large">
                      <v-icon start>mdi-send</v-icon>
                      {{ $t ? $t('contact-forms.form.submit') : 'Request Demo' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </div>
      </v-container>
    </v-main>
  </v-app>
</template>
<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getLoginBackgroundImage, getLoginLogoImage } from '@/assets/Branding/branding.js'
import { setCookie, getCookie } from '@/services/utils'
import usa_flag from '@/assets/General/usa_flag.svg'
import german_flag from '@/assets/General/german_flag.svg'

import { createDemoSignup, authenticate } from '@/controllers/DemoSignupsController'

const router = useRouter()
const { t, locale } = useI18n()

const backgroundImage = ref('')
const loginLogo = ref('')
const currentLanguage = ref('de')
const formRef = ref(null)
const isSubmitting = ref(false)
const submitStatus = ref('')
const errorMessage = ref('')
const formError = ref('')

const formData = reactive({
  name: '',
  email: '',
  company: '',
  phone: '',
  message: ''
})

const rules = {
  required: v => !!v || t('contact-forms.validation.required'),
  minLength: min => v => (v && v.length >= min) || t('contact-forms.validation.minLength', { min }),
  email: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || t('contact-forms.validation.invalidEmail'),
  maxLength: max => v => !v || v.length <= max || t('contact-forms.validation.maxLength', { max })
}

const changeLanguage = async (lang) => {
  currentLanguage.value = lang
  locale.value = lang
  await setCookie('language', lang, 365)
}

const loadLanguage = async () => {
  const saved = await getCookie('language')
  const lang = saved || (navigator.language.startsWith('de') ? 'de' : 'en')
  currentLanguage.value = lang
  locale.value = lang
  if (!saved) await setCookie('language', lang, 365)
}

const goBack = () => router.push('/login')

const resetForm = () => {
  Object.assign(formData, { name: '', email: '', company: '', phone: '', message: '' })
  submitStatus.value = ''
  errorMessage.value = ''
  formError.value = ''
  formRef.value?.resetValidation()
}

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) {
    formError.value = t('contact-forms.validation.fillRequired')
    return
  }

  isSubmitting.value = true
  formError.value = ''

  try {
    await createDemoSignup({
      name: formData.name,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      message: formData.message
    })

    submitStatus.value = 'success'

    setTimeout(async () => {
      try {
        await authenticate(formData.email.trim().toLowerCase(), { 
          skipLogin: true,
          locale: currentLanguage.value 
        })
      } catch {
        router.push('/login')
      }
    }, 2200)

  } catch (err) {
    submitStatus.value = 'error'
    errorMessage.value = err.response?.data?.message || t('contact-forms.error.unexpected')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => {
  try {
    backgroundImage.value = await getLoginBackgroundImage()
    loginLogo.value = await getLoginLogoImage()
  } catch (e) {
    console.warn('Failed to load branding images')
  }
  await loadLanguage()
})
</script>
<style scoped>
  .request-demo-container {
    position: relative;
    min-height: 100vh;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .background-layer {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }

  .background-image {
    width: 100%;
    height: 100%;
  }

  .background-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(33, 150, 243, 0.3), rgba(21, 101, 192, 0.25));
    backdrop-filter: blur(2px);
  }

  .content-wrapper {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
    padding: 20px;
  }

  .back-button {
    position: fixed;
    top: 24px;
    left: 24px;
    z-index: 10;
    background: rgba(255, 255, 255, 0.2) !important;
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
  }

  .back-button:hover {
    background: rgba(255, 255, 255, 0.3) !important;
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateX(-4px);
  }

  .language-toggle-wrapper {
    position: fixed;
    top: 24px;
    right: 24px;
    z-index: 10;
  }

  .language-toggle {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .language-toggle .v-btn {
    color: #64748b !important;
    transition: all 0.3s ease;
    min-width: 60px !important;
    padding: 6px 10px !important;
  }

  .language-toggle .v-btn.active-lang {
    background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(21, 101, 192, 0.15)) !important;
    color: #2196F3 !important;
    font-weight: 600;
  }

  .welcome-text {
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .page-title {
    font-size: 2.5rem;
    letter-spacing: -0.5px;
  }

  .page-subtitle {
    font-size: 1.1rem;
    opacity: 0.95;
  }

  .demo-card {
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .card-content {
    padding: 40px;
  }

  .logo-section {
    margin-bottom: 24px;
  }

  .flag-img {
    border-radius: 4px;
    object-fit: cover;
  }

  .form-intro {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    background: linear-gradient(135deg, rgba(33, 150, 243, 0.08), rgba(21, 101, 192, 0.08));
    border-radius: 10px;
    border-left: 4px solid #2196F3;
  }

  .form-intro-text {
    font-size: 13.5px;
    color: #424242;
    line-height: 1.5;
  }

  .form-actions {
    margin-top: 20px;
  }

  .status-container {
    text-align: center;
    padding: 48px 24px;
  }

  .success-animation,
  .error-animation {
    margin-bottom: 24px;
    animation: scaleIn 0.5s ease-out;
  }

  @keyframes scaleIn {
    from {
      transform: scale(0);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .status-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
  }

  .error-title {
    color: #dc2626;
  }

  .status-message {
    font-size: 16px;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
  }

  @media (max-width: 960px) {
    .content-wrapper {
      max-width: 600px;
      padding: 16px;
    }

    .card-content {
      padding: 32px 24px;
    }

    .page-title {
      font-size: 2rem;
    }

    .page-subtitle {
      font-size: 1rem;
    }
  }

  @media (max-width: 599px) {
    .request-demo-container {
      align-items: flex-start;
      padding-top: 80px;
    }

    .content-wrapper {
      max-width: 100%;
      padding: 12px;
    }

    .card-content {
      padding: 24px 20px;
    }

    .logo-section {
      margin-bottom: 20px;
    }

    .logo-section .v-img {
      max-height: 56px !important;
    }

    .back-button {
      top: 16px;
      left: 16px;
    }

    .language-toggle-wrapper {
      top: 16px;
      right: 16px;
    }

    .language-toggle .v-btn {
      font-size: 12px;
      min-width: 52px !important;
      padding: 5px 8px !important;
    }

    .welcome-text {
      margin-bottom: 20px !important;
    }

    .page-title {
      font-size: 1.75rem !important;
      line-height: 1.2;
    }

    .page-subtitle {
      font-size: 0.95rem !important;
    }

    .form-intro {
      padding: 12px 14px;
      margin-bottom: 16px !important;
    }

    .form-intro-text {
      font-size: 12.5px;
    }

    .form-actions {
      margin-top: 16px;
    }

    .status-container {
      padding: 32px 16px;
    }

    .status-title {
      font-size: 20px;
    }

    .status-message {
      font-size: 14px;
    }

    .success-animation .v-icon,
    .error-animation .v-icon {
      font-size: 64px !important;
    }
  }

  @media (max-width: 374px) {
    .content-wrapper {
      padding: 10px;
    }

    .card-content {
      padding: 20px 16px;
    }

    .page-title {
      font-size: 1.5rem !important;
    }

    .page-subtitle {
      font-size: 0.875rem !important;
    }

    .logo-section .v-img {
      max-height: 48px !important;
    }

    .form-intro {
      padding: 10px 12px;
    }

    .form-intro-text {
      font-size: 11.5px;
    }

    .back-button {
      top: 12px;
      left: 12px;
    }

    .language-toggle-wrapper {
      top: 12px;
      right: 12px;
    }
  }
</style>
